/**
 * PATH: packages/kanban-host/src/allocation/findRecentMirrorEvent.ts
 * FILE_ID: KH-ALLOC-FIND-MIRROR-001
 * PURPOSE: Pure I→O helper that inspects a freshly-returned Card's
 *          `audit_log_json` for a recent `mirrored_to` event written
 *          by the Newmark Stack 1 dual-board routing path.
 * WHY:     The BE silently mirrors the primary card onto a job board
 *          when ``OrganisationSettings.email_kanban_dual_board_routing``
 *          is on. Until now the operator had no FE feedback that the
 *          mirror happened — they had to know to look. This helper
 *          + the toast it powers close that gap by reading the
 *          card's own audit trail rather than introducing a new BE
 *          response contract.
 *
 * FLOW:
 *   ┌──────────────────┐   ┌─────────────────────────┐   ┌─────────────────┐
 *   │ Card returned by │ → │ scan audit_log_json for │ → │ MirrorEvent |   │
 *   │ allocateEmailTo… │   │ event === 'mirrored_to' │   │ null            │
 *   └──────────────────┘   │ at within freshness win │   └─────────────────┘
 *                          └─────────────────────────┘
 */

/**
 * Subset of the `Card.audit_log_json` entry shape we read here.
 * Kept loose because the underlying BE column is JSONB and other
 * code paths may add fields to existing entries.
 */
export interface MirrorAuditEntry {
  event: 'mirrored_to' | string
  at: string
  mirror_card_id?: string
  target_board_id?: string
  target_lane_id?: string
  by?: string
}

export interface CardWithAuditLog {
  audit_log_json?: ReadonlyArray<Record<string, unknown>>
}

export interface MirrorEventLookupOptions {
  /**
   * Maximum age (in ms) of the audit entry to count as "fresh".
   * Defaults to 30 s — long enough to survive a slow round-trip
   * but short enough that an unrelated old mirror doesn't fire
   * a stale toast on the next allocate.
   */
  freshnessMs?: number
  /** Pure injection seam so tests don't need to mock Date.now. */
  nowMs?: number
}

const DEFAULT_FRESHNESS_MS = 30_000

/**
 * Return the most recent `mirrored_to` audit-log entry on `card`
 * if it's "fresh" per `freshnessMs`. Returns `null` when:
 *   - the card has no audit log
 *   - no entry has `event === 'mirrored_to'`
 *   - the most recent matching entry is older than the freshness window
 *   - the entry's `at` field is unparseable
 *
 * Pure: depends only on its arguments + the injected `nowMs`.
 */
export function findRecentMirrorEvent(
  card: CardWithAuditLog | null | undefined,
  options: MirrorEventLookupOptions = {},
): MirrorAuditEntry | null {
  if (!card || !Array.isArray(card.audit_log_json)) return null

  const now = options.nowMs ?? Date.now()
  const window = options.freshnessMs ?? DEFAULT_FRESHNESS_MS

  // Walk newest-last in the log; we want the latest matching entry.
  // The audit log is append-only on the BE so iterating in reverse
  // is the cheapest way to find "most recent".
  for (let i = card.audit_log_json.length - 1; i >= 0; i--) {
    const entry = card.audit_log_json[i]
    if (!entry || typeof entry !== 'object') continue
    if (entry.event !== 'mirrored_to') continue
    const rawAt = entry.at
    if (typeof rawAt !== 'string') continue
    const ts = Date.parse(rawAt)
    if (!Number.isFinite(ts)) continue
    if (now - ts > window) {
      // Older than the window — and since the log is monotonic,
      // every earlier matching entry is even older. Stop.
      return null
    }
    return entry as MirrorAuditEntry
  }
  return null
}

/**
 * Build the operator-facing toast copy for a detected mirror event.
 * Pure so the snapshot is testable without rendering. Keeps the
 * surface tiny because the operator-facing variants are few.
 */
export function formatMirrorToastMessage(entry: MirrorAuditEntry): string {
  // We deliberately don't lean on the target_board_id UUID here —
  // operators don't recognise UUIDs and the BE audit-log shape
  // doesn't carry a human board name (see Stack 1 audit-log shape
  // in email_kanban/services/card_mirror.py). A generic line is
  // honest + actionable; the operator can open the card to see
  // the CardMirror row if they need the exact target.
  return 'Mirrored to job board — the card lives on both lists.'
}
